import { GetRecoilValue } from "recoil";
import { selector } from "./recoilSelectors";
import { atomWithPersistence } from "../utils/persistence";
import { EMPTY_FILTER_SELECTION, FilterSelection } from "../types/filters";
import { activeFilterKeys } from "../utils/filters";
// read only inside selectors and functions: utils/jobFilters sits on an import loop with jobsState
import { JOB_FILTER, jobPassesFilters, SERVER_JOB_FILTER_KEYS } from "../utils/jobFilters";
import { tripsState } from "./tripsState";
import { TripActiveStatus, TripProgressStatus } from "../types/trip";
import { Job } from "../types/job";
import { filterClockState } from "./filterClockState";
import { timezoneState } from "./accountState";

/** The Jobs bar's filters, shared by the dispatch board and the jobs page. */
export const jobFiltersState = atomWithPersistence<FilterSelection>("filters.jobs", EMPTY_FILTER_SELECTION);

/** ETAs of the trips under way, by the job they head to. */
export const etaByJobIdState = selector<Map<string, number>>({
    key: "filters.jobs.eta",
    get: ({ get }) => new Map(get(tripsState)
        .filter(trip => trip.jobId && trip.progressStatus === TripProgressStatus.IN_PROGRESS && trip.activeStatus === TripActiveStatus.ACTIVE)
        .map(trip => [trip.jobId, trip.etaTs])),
});

/**
 * The jobs a list keeps under the Jobs bar's filters. Reads the ETAs only while the Late filter is
 * on, so a moving trip does not re-run every job list.
 */
export function filterJobsBySelection(get: GetRecoilValue, jobs: Job[]): Job[] {
    const selection = get(jobFiltersState);
    const keys = activeFilterKeys(selection);
    if (keys.length === 0) {
        return jobs;
    }
    return jobs.filter(job => jobPassesFilters(job, selection, jobFilterContext(get, keys)));
}

/** What the job filters read besides the job: the clock, the account's time zone and the ETAs, only while Late is on. */
export function jobFilterContext(get: GetRecoilValue, keys: string[]) {
    const late = keys.includes(JOB_FILTER.LATE);
    return {
        now: late ? Math.max(get(filterClockState), Date.now()) : Date.now(),
        etaByJobId: late ? get(etaByJobIdState) : undefined,
        timezone: late ? get(timezoneState) : undefined,
    };
}


export const serverJobFiltersState = selector<Record<string, string[]> | undefined>({
    key: "filters.jobs.server",
    get: ({ get }) => {
        const selection = get(jobFiltersState);
        const keys = activeFilterKeys(selection).filter(key => SERVER_JOB_FILTER_KEYS.includes(key));
        return keys.length > 0 ? Object.fromEntries(keys.map(key => [key, selection.values[key]])) : undefined;
    },
});

/** The ETAs, but only while the Late filter is on, so a moving trip does not re-count the Jobs bar. */
export const etaWhileLateState = selector<Map<string, number> | undefined>({
    key: "filters.jobs.eta.late",
    get: ({ get }) => activeFilterKeys(get(jobFiltersState)).includes(JOB_FILTER.LATE) ? get(etaByJobIdState) : undefined,
});
